// UserGeneralDetailsComponent.ts
import {CommonModule, NgClass} from '@angular/common';
import {Component, inject, input} from '@angular/core';
import {FormGroup, ReactiveFormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {FormControlCollectorDirective} from '../../../directives/form-control-collector.directive';
import {UserFormFactory} from '../../../services/user-form.factory';
import {UserFormErrorComponent} from '../components/user-form-error.component';
import {AutoTabIndexDirective} from '@shared/ui';

@Component({
	selector: 'dc-user-form-general-details',
	standalone: true,
	styleUrls: ['../user-form.styles.scss'],
	imports: [ReactiveFormsModule, AutoTabIndexDirective, CommonModule, NgClass, FormControlCollectorDirective, TranslateModule, UserFormErrorComponent],
	template: `
		<!-- Keep only the general fields here -->
		<div
			class="section-fields-container"
			[formGroup]="parentForm()"
			id="general-details-section"
		>
			<!-- User Name -->
			<div class="form-field-container">
				<label for="username">
					{{ 'struct.user-edit.username' | translate }}
					<span class="required-field">*</span>
				</label>
				<input
					id="username"
					type="text"
					formControlName="username"
					dcFormControlCollector
					sectionId="general-details"
					[ngClass]="{'error-input': isInvalid('username')}"
					autocomplete="username"
					autoTabIndex
				/>
				<dc-user-form-error
					[parentForm]="parentForm()"
					controlSection="GeneralDetails"
					controlName="username"
					[isInvalidFn]="isInvalid"
					[additionalErrors]="{
						invalidUsername: 'struct.user-edit.errors.invalidusername-char',
						usernameExists: 'struct.user-edit.errors.usernameexists',
					}"
				/>
			</div>

			<!-- Full Name -->
			<div class="form-field-container">
				<label for="fullname">
					{{ 'user.form.field.fullname' | translate }}
				</label>
				<input
					id="fullname"
					type="text"
					formControlName="fullname"
					dcFormControlCollector
					sectionId="general-details"
					[ngClass]="{'error-input': isInvalid('fullname')}"
					autocomplete="given-name"
					autoTabIndex
				/>
				<dc-user-form-error
					[parentForm]="parentForm()"
					controlSection="GeneralDetails"
					controlName="fullname"
					[isInvalidFn]="isInvalid"
				/>
			</div>

			<!-- Company -->
			<div class="form-field-container">
				<label for="company">
					{{ 'user.form.field.company' | translate }}
				</label>
				<input
					id="company"
					type="text"
					formControlName="company"
					dcFormControlCollector
					sectionId="general-details"
					[ngClass]="{'error-input': isInvalid('company')}"
					autocomplete="organization"
					autoTabIndex
				/>
				<dc-user-form-error
					[parentForm]="parentForm()"
					controlSection="GeneralDetails"
					controlName="company"
					[isInvalidFn]="isInvalid"
				/>
			</div>

			<!-- Title -->
			<div class="form-field-container">
				<label for="title">
					{{ 'user.form.field.title' | translate }}
				</label>
				<input
					id="title"
					type="text"
					formControlName="title"
					dcFormControlCollector
					sectionId="general-details"
					[ngClass]="{'error-input': isInvalid('title')}"
					autoTabIndex
				/>
				<dc-user-form-error
					[parentForm]="parentForm()"
					controlSection="GeneralDetails"
					controlName="title"
					[isInvalidFn]="isInvalid"
				/>
			</div>

			<!-- Department -->
			<div class="form-field-container">
				<label for="department">
					{{ 'user.form.field.department' | translate }}
				</label>
				<input
					id="department"
					type="text"
					formControlName="department"
					dcFormControlCollector
					sectionId="general-details"
					[ngClass]="{'error-input': isInvalid('department')}"
					autocomplete="organization-title"
					autoTabIndex
				/>
				<dc-user-form-error
					[parentForm]="parentForm()"
					controlSection="GeneralDetails"
					controlName="department"
					[isInvalidFn]="isInvalid"
				/>
			</div>

			<!-- Notes (Spanning two columns) -->
			<div class="form-field-container md:col-span-2">
				<label for="notes">
					{{ 'user.form.field.notes' | translate }}
				</label>
				<textarea
					id="notes"
					formControlName="notes"
					dcFormControlCollector
					sectionId="general-details"
					[ngClass]="{'error-input': isInvalid('notes')}"
					autoTabIndex
				></textarea>
				<dc-user-form-error
					[parentForm]="parentForm()"
					controlSection="GeneralDetails"
					controlName="notes"
					[isInvalidFn]="isInvalid"
				/>
			</div>
		</div>
	`,
})
export class UserFormGeneralDetailsComponent {
	private readonly userFormFactory = inject(UserFormFactory);
	protected readonly isInvalid = this.userFormFactory.isInvalidFnFactory<'GeneralDetails'>({checkDirty: false});

	readonly parentForm = input.required<FormGroup>();
}
